import {
  useGenericRefinement,
  BaseRefinementItem,
  RefinementConfig,
  RawRefinementItem,
} from '../../Algolia/useGenericRefinement';

export const DOK_MAP = {
  1: 'Recall',
  2: 'Skills and Concepts',
  3: 'Strategic Thinking',
  4: 'Extended Thinking',
} as const;

export interface DokRefinementItem extends BaseRefinementItem {
  dokLevel: number;
  dokName: string;
}

export interface DokMapping {
  levelMap: typeof DOK_MAP;
}

export interface DokRefinementResult {
  items: DokRefinementItem[];
  refine: (value: string) => void;
  refineMultiple: (values: string[]) => void;
  clearRefinements: () => void;
  isSearchStalled: boolean;
  canRefine: boolean;
  getCountForLevel: (level: number) => number;
  getTotalCount: () => number;
}

// Configuration for DOK refinement
const dokConfig: RefinementConfig<DokRefinementItem, DokMapping> = {
  attribute: 'Depth Of Knowledge',
  mapping: {
    levelMap: DOK_MAP,
  },

  validateItem: (item: RawRefinementItem): boolean =>
    parseInt(item.value, 10) >= 1 && parseInt(item.value, 10) <= 4,

  transformItem: (item: RawRefinementItem, mapping: DokMapping): DokRefinementItem => {
    const level = parseInt(item.value, 10);
    const name = mapping.levelMap[level as keyof typeof mapping.levelMap] || 'Unknown';

    return {
      label: `${level} - ${name}`,
      value: item.value,
      count: item.count,
      isRefined: item.isRefined,
      dokLevel: level,
      dokName: name,
    };
  },

  sortItems: (a: DokRefinementItem, b: DokRefinementItem): number =>
    a.dokLevel - b.dokLevel,

  getters: {
    getCountForLevel: (items: DokRefinementItem[], level: number): number => {
      const item = items.find((dokItem) => dokItem.dokLevel === level);
      return item?.count || 0;
    },
  },
};

/**
 * Custom hook for Depth of Knowledge (DOK) refinements
 * Provides Algolia facet data specifically formatted for DOK levels
 */
export const useDokRefinement = (
  attribute = 'Depth Of Knowledge',
): DokRefinementResult => {
  const config = { ...dokConfig, attribute };
  const result = useGenericRefinement<DokRefinementItem, DokMapping>(config);

  return {
    ...result,
    getCountForLevel: result.getCountForLevel,
    getTotalCount: () => result.items.reduce((sum, item) => sum + item.count, 0),
  };
};
